import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCog, Plus, Power, KeyRound, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import LinkMyProfile from "@/components/LinkMyProfile";

const isStrongPassword = (pw) => pw.length >= 8 && /[A-Z]/.test(pw) && /[^A-Za-z0-9]/.test(pw);

export default function Users() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "HR_MANAGER" });
  const [pwOpen, setPwOpen] = useState(null);
  const [pw, setPw] = useState("");

  const { data, isLoading } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });

  const create = useMutation({
    mutationFn: async () => (await api.post("/users", form)).data,
    onSuccess: () => { toast.success("Account created"); qc.invalidateQueries({ queryKey: ["users"] }); setOpen(false); setForm({ name: "", email: "", password: "", role: "HR_MANAGER" }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const toggle = useMutation({
    mutationFn: async (id) => (await api.patch(`/users/${id}/status`)).data,
    onSuccess: () => { toast.success("Status updated"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const resetPw = useMutation({
    mutationFn: async () => (await api.post(`/users/${pwOpen}/reset-password`, { new_password: pw })).data,
    onSuccess: () => { toast.success("Password reset"); setPwOpen(null); setPw(""); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader overline="Access Control" title="User Accounts" icon={UserCog}
        subtitle="Super Admin manages HR Manager accounts. All actions are audited."
        actions={<Button onClick={() => setOpen(true)} className="bg-navy hover:bg-navy-700 btn-lift" data-testid="add-user-btn"><Plus className="h-4 w-4 mr-1" /> Create Account</Button>}
      />
      <LinkMyProfile />
      {isLoading ? <Loading /> : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Name</TableHead>
              <TableHead className="overline text-slate-500">Email</TableHead>
              <TableHead className="overline text-slate-500">Role</TableHead>
              <TableHead className="overline text-slate-500">Status</TableHead>
              <TableHead className="text-right overline text-slate-500">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(data || []).map((u) => (
                <TableRow key={u.id} className="table-row-i" data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium text-navy flex items-center gap-2">
                    {u.role === "SUPER_ADMIN" && <ShieldAlert className="h-4 w-4 text-teal" />}{u.name}
                  </TableCell>
                  <TableCell className="text-slate-600">{u.email}</TableCell>
                  <TableCell className="text-slate-600">{{ SUPER_ADMIN: "Super Admin", HR_MANAGER: "HR Manager", EMPLOYEE: "Employee", SERVICE_PROVIDER: "Service Provider" }[u.role] || u.role}</TableCell>
                  <TableCell><StatusBadge status={u.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setPwOpen(u.id)} data-testid={`reset-pw-${u.email}`}><KeyRound className="h-4 w-4" /></Button>
                    {u.id !== user.id && (
                      <Button size="sm" variant="ghost" className={u.status === "Active" ? "text-red-500" : "text-emerald-600"} onClick={() => toggle.mutate(u.id)} data-testid={`toggle-${u.email}`}><Power className="h-4 w-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Create Account</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Full Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="user-name" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="user-email" /></div>
            <div><Label>Password * (min 8, 1 capital, 1 special character)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="user-password" /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="HR_MANAGER">HR Manager</SelectItem>
                  <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!form.name || !form.email || !isStrongPassword(form.password) || create.isPending} onClick={() => create.mutate()} data-testid="save-user">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwOpen} onOpenChange={(o) => !o && setPwOpen(null)}>
        <DialogContent className="bg-white" data-testid="pw-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Reset Password</DialogTitle></DialogHeader>
          <div><Label>New Password (min 8, 1 capital, 1 special character)</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1" data-testid="new-pw" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(null)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!isStrongPassword(pw) || resetPw.isPending} onClick={() => resetPw.mutate()} data-testid="submit-pw">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
